// src/config/seo.ts
import { SupportedLanguage } from './routes';

export const SEO_CONFIG = {
  siteName: 'White Screen Online',
  baseUrl: 'https://whitescreenonline.org',
  defaultImage: '/og-image.png',
  googleSiteVerification: 'google-site-verification=KqV5t3a4pSxj61K5o8sk2fNrF8pLXeX2zAvub-PbU7o',

  defaultColorNames: {
    en: 'White Screen',
    es: 'Pantalla blanca',
    fr: 'Écran blanc',
    de: 'Weißer Bildschirm',
    it: 'Schermo bianco',
    pl: 'Biały ekran',
    pt: 'Tela branca',
    'pt-BR': 'Tela branca',
    nl: 'Wit scherm',
    ja: '白い画面'
  } as const,

  openGraph: {
    type: 'website',
    site_name: 'White Screen Online'
  },

  meta: {
    en: {
      title: 'White Screen - Full Screen Tool',
      description: 'Create fullscreen color backgrounds for presentations, recording, or privacy. Free tool for displaying solid colors in fullscreen mode.',
      keywords: ['white screen', 'fullscreen color', 'presentation background', 'screen test', 'color background', 'privacy screen']
    },
    es: {
      title: 'Pantalla blanca - Herramienta de Pantalla Completa',
      description: 'Crea fondos de color a pantalla completa para presentaciones, grabaciones o privacidad. Herramienta gratuita.',
      keywords: ['pantalla blanca', 'color pantalla completa', 'fondo presentación', 'prueba de pantalla', 'fondo de color', 'pantalla privada']
    },
    fr: {
      title: 'Écran blanc - Outil Plein Écran',
      description: 'Créez des arrière-plans de couleur en plein écran pour les présentations, l\'enregistrement ou la confidentialité. Outil gratuit.',
      keywords: ['écran blanc', 'couleur plein écran', 'fond présentation', 'test écran', 'fond coloré', 'écran privé']
    },
    de: {
      title: 'Weißer Bildschirm - Vollbild Werkzeug',
      description: 'Erstellen Sie Vollbild-Farbhintergründe für Präsentationen, Aufnahmen oder Privatsphäre. Kostenloses Werkzeug.',
      keywords: ['weißer bildschirm', 'vollbild-farbe', 'präsentationshintergrund', 'bildschirmtest', 'farbhintergrund', 'privatsphäre-bildschirm']
    },
    it: {
      title: 'Schermo bianco - Strumento Schermo Intero',
      description: 'Crea sfondi colorati a schermo intero per presentazioni, registrazioni o privacy. Strumento gratuito.',
      keywords: ['schermo bianco', 'colore schermo intero', 'sfondo presentazione', 'test schermo', 'sfondo colorato', 'schermo privacy']
    },
    pl: {
      title: 'Biały ekran - Narzędzie Pełnoekranowe',
      description: 'Twórz kolorowe tła pełnoekranowe do prezentacji, nagrywania lub prywatności. Darmowe narzędzie.',
      keywords: ['biały ekran', 'kolor pełnoekranowy', 'tło prezentacji', 'test ekranu', 'kolorowe tło', 'ekran prywatności']
    },
    pt: {
      title: 'Tela branca - Ferramenta Tela Cheia',
      description: 'Crie fundos coloridos em tela cheia para apresentações, gravações ou privacidade. Ferramenta gratuita.',
      keywords: ['tela branca', 'cor tela cheia', 'fundo apresentação', 'teste de tela', 'fundo colorido', 'tela privada']
    },
    'pt-BR': {
      title: 'Tela branca - Ferramenta Tela Cheia',
      description: 'Crie fundos coloridos em tela cheia para apresentações, gravações ou privacidade. Ferramenta gratuita.',
      keywords: ['tela branca', 'cor tela cheia', 'fundo apresentação', 'teste de tela', 'fundo colorido', 'tela privada']
    },
    nl: {
      title: 'Wit scherm - Volledig Scherm Hulpmiddel',
      description: 'Maak volledig scherm kleurachtergronden voor presentaties, opnames of privacy. Gratis hulpmiddel.',
      keywords: ['wit scherm', 'volledig scherm kleur', 'presentatie achtergrond', 'schermtest', 'kleurachtergrond', 'privacy scherm']
    },
    ja: {
      title: '白い画面 - フルスクリーンツール',
      description: 'プレゼンテーション、録画、プライバシーのためのフルスクリーンカラー背景を作成。無料ツール。',
      keywords: ['白い画面', 'フルスクリーンカラー', 'プレゼンテーション背景', '画面テスト', 'カラー背景', 'プライバシー画面']
    }
  }
} as const;

export type SeoConfig = typeof SEO_CONFIG;

interface MetaTagsResult {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogImage: string;
}

export const generateMetaTags = (
  lang: SupportedLanguage,
  color: string,
  colorName: string
): MetaTagsResult => {
  const defaultMeta = SEO_CONFIG.meta[lang];
  const defaultColorName = SEO_CONFIG.defaultColorNames[lang];

  return {
    title: defaultMeta.title.replace(defaultColorName, colorName),
    description: defaultMeta.description,
    keywords: defaultMeta.keywords.map(keyword => 
      keyword.startsWith(defaultColorName.toLowerCase())
        ? keyword.replace(defaultColorName.toLowerCase(), colorName.toLowerCase())
        : keyword
    ).join(', '),
    canonical: `${SEO_CONFIG.baseUrl}${lang === 'en' ? '' : `/${lang}`}${color ? `/${color}` : ''}`,
    ogImage: `${SEO_CONFIG.baseUrl}${SEO_CONFIG.defaultImage}`
  };
};

export const generateCanonicalUrl = (
  lang: SupportedLanguage,
  path: string = ''
): string => {
  const langPrefix = lang === 'en' ? '' : `/${lang}`;
  return `${SEO_CONFIG.baseUrl}${langPrefix}${path}`;
};

interface StructuredData {
  '@context': 'https://schema.org';
  '@type': 'WebApplication';
  name: string;
  description: string;
  applicationCategory: 'UtilityApplication';
  url: string;
  inLanguage: string;
  offers: {
    '@type': 'Offer';
    price: '0';
    priceCurrency: 'USD';
  };
}

export const generateStructuredData = (
  lang: SupportedLanguage,
  color: string,
  colorName: string
): StructuredData => {
  const meta = SEO_CONFIG.meta[lang];
  const defaultColorName = SEO_CONFIG.defaultColorNames[lang];
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: meta.title.replace(defaultColorName, colorName),
    description: meta.description,
    applicationCategory: 'UtilityApplication',
    url: generateCanonicalUrl(lang, color ? `/${color}` : ''),
    inLanguage: formatLanguageForSchema(lang),
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    }
  };
};

const formatLanguageForSchema = (lang: SupportedLanguage): string => {
  return lang === 'pt-BR' ? 'pt-BR' : lang;
};